import { useCallback, useEffect } from "react";
import { TRUSTLY_PROCEED_TO_CHOOSE_ACCOUNT } from "../../consts";

/**
 * Listens for the `trustlyProceedToChooseAccount` postMessage that the
 * Trustly OAuth pop-up sends when the user finishes the bank login flow.
 * On a valid message we tell the lightbox to advance to its
 * account-chooser view via `window.Trustly.proceedToChooseAccount`.
 *
 * The owning component supplies an `isAllowedOrigin` predicate that gates
 * which `event.origin` values we trust. Without that check any iframe on
 * the page could force the lightbox to advance by posting the magic
 * string. Defining the origin policy outside this hook keeps the hook
 * testable and decoupled from how the Trustly endpoint URL is sourced.
 */
export const useTrustlyOauthMessage = (
  isAllowedOrigin: (origin: string) => boolean
) => {
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event?.data !== TRUSTLY_PROCEED_TO_CHOOSE_ACCOUNT) {
        return;
      }
      if (!isAllowedOrigin(event.origin)) {
        return;
      }
      window.Trustly?.proceedToChooseAccount?.();
    },
    [isAllowedOrigin]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage, false);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);
};

export default useTrustlyOauthMessage;
